import {Button, Checkbox, Modal, Radio} from 'antd'
import {getId, getType} from 'apis/model/base'
import {createValue} from 'components/form/utils'
import {CodiDocumentRowSelect, CodiDocumentSelect,} from 'components/RichSlateEditor/utils/useEditorUtils'
import {ROOT_URL} from 'envs/_current/config'
import {Formik} from 'formik'
import getLinkToDetail from 'helpers/getLinkToDetail'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo} from 'react'
import {FaBookBible} from 'react-icons/fa6'
import {RiSettings3Line} from 'react-icons/ri'
import {getLinkToDetailRow} from 'views/AppLocals/Codi/codiHelpers'
import {AppEntities} from 'views/AppLocals/enums'
import {NullAsDefault, renderIf} from 'views/Shared'
import {MentionRenderer} from '.'
import {codi_content_options, createCodiContent} from '../../codi'

function withPath(path) {
  return path ?? ROOT_URL
}

const ModalContent = ({ onCancel, onSubmit = NullAsDefault }) => {
  const t = useTranslate()

  const transform = useCallback((values) => {
    let url,
      content,
      rows = [],
      title = ''
    const { codi_document, codi_document_row, name_option } = values

    const mention_options = Array.from(values?.mention_options ?? [])

    const target = codi_document_row ?? codi_document

    const type = getType(target)

    const display_short_name =
      name_option === codi_content_options.short_name.value

    const link_attached = mention_options.includes(
      codi_content_options.attach_link.value
    )

    const show_detail = mention_options.includes(
      codi_content_options.sub_content.value
    )

    const show_content = mention_options.includes(
      codi_content_options.content.value
    )

    if (show_content) {
      content = target?.content
    }

    if (codi_document) {
      title = _.get(codi_document, display_short_name ? 'document_no' : 'title')
    }

    switch (type) {
      case AppEntities.CODI_DOCUMENT:
        if (link_attached) {
          url = withPath(getLinkToDetail(target))
        }
        break
      case AppEntities.CODI_DOCUMENT_ROW:
        const sub_title = _.get(
          target,
          display_short_name ? 'short_index_name' : 'index_name'
        )
        title = [title, sub_title].join(sub_title ? ' - ' : '')
        if (show_detail) {
          const sub_content = createCodiContent(target, t)
          rows = String(sub_content ?? '')
            .split('\n')
            .filter((e) => e.length)
        }
        if (link_attached) {
          const { pathname, search, hash } =
            getLinkToDetailRow(codi_document_row) ?? {}
          url = withPath([pathname, search, hash].join(''))
        }
        break
      default:
        break
    }

    const [mention_id, mention_type] = [getId(target), getType(target)]

    return {
      url,
      rows,
      title,
      content,
      mention_type,
      data: {
        owner_id: mention_id,
        owner_type: mention_type,
      },
    }
  }, [])

  return (
    <div className="flex flex-col gap-3 mt-6">
      <div className="flex items-center gap-3 mb-1">
        <FaBookBible
          size={20}
          className="anticon text-primary-300"
        />
        <p className="font-medium text-color-200 italic">
          {t(
            'choose the book title (and chapter, sentence) you want to mention'
          )}
        </p>
      </div>
      <Formik
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true)
          onSubmit(
            _.omitBy(transform(values), _.isUndefined, () => {
              setSubmitting(false)
            })
          )
        }}
        initialValues={{
          mention_type: undefined,
          codi_document: undefined,
          codi_document_row: undefined,
          name_option: codi_content_options.name.value,
          mention_options: [
            codi_content_options.content.value,
            codi_content_options.attach_link.value,
          ],
        }}>
        {({ values, handleChange, setFieldValue, handleSubmit }) => {
          const {
            name_option,
            codi_document,
            mention_options,
            codi_document_row,
          } = values ?? {}
          return (
            <React.Fragment>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-color-300 italic">
                  {t('choose book')}
                </span>
                <CodiDocumentSelect
                  placeholder={t('book title')}
                  onChange={(item, option) => {
                    setFieldValue('codi_document_row', null)
                    setFieldValue('codi_document', option ?? null)
                  }}
                />
              </div>
              {renderIf(
                codi_document,
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-color-300 italic">
                    {t('choose chapter sentence')}
                  </span>
                  <CodiDocumentRowSelect
                    placeholder={t('detail')}
                    codi_document_id={getId(codi_document)}
                    onChange={(item, option) => {
                      handleChange(
                        createValue('codi_document_row', option ?? null)
                      )
                    }}>
                    <div className="flex flex-col mt-3">
                      <div className="flex items-center gap-1 mb-1">
                        <RiSettings3Line
                          size={14}
                          className="text-color-200"
                        />
                        <span className="font-medium text-sm text-color-200 italic">
                          {`${t('display options')}: `}
                        </span>
                      </div>
                      <div className="flex flex-col ml-2">
                        <Radio.Group
                          name="name_option"
                          onChange={handleChange}
                          defaultValue={name_option}
                          className="flex items-center gap-3">
                          {[
                            codi_content_options.name,
                            codi_content_options.short_name,
                          ].map(({ label, value }, index) => (
                            <Radio
                              className="py-1 text-sm text-color-300 italic"
                              key={index}
                              label={t(label)}
                              value={value}>
                              {t(label)}
                            </Radio>
                          ))}
                        </Radio.Group>
                        <Checkbox.Group
                          name="mention_options"
                          className="grid grid-cols-1 gap-2"
                          defaultValue={mention_options}
                          onChange={(checked_values) => {
                            handleChange(
                              createValue('mention_options', checked_values)
                            )
                          }}>
                          {(codi_document_row
                            ? [
                                codi_content_options.content,
                                codi_content_options.sub_content,
                                codi_content_options.attach_link,
                              ]
                            : [codi_content_options.attach_link]
                          ).map(({ label, value }, index) => (
                            <Checkbox
                              key={index}
                              value={value}
                              label={t(label)}
                              style={{ margin: 0 }}
                              className="text-sm text-color-300 italic">
                              {t(label)}
                            </Checkbox>
                          ))}
                        </Checkbox.Group>
                      </div>
                    </div>
                  </CodiDocumentRowSelect>
                  <div className="flex flex-col gap-2 mt-1">
                    <div className="border-b text-sm text-color-200 italic font-medium">
                      {t('in preview1')}
                    </div>
                    <div className="SlatePreview flex flex-col px-2">
                      <MentionRenderer
                        preview={true}
                        mention_type={getType(
                          codi_document_row ?? codi_document
                        )}
                        {...(transform(values) ?? {})}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div
                className="flex gap-3"
                style={{ marginTop: 24 }}>
                <div className="flex-1" />
                <Button
                  type="default"
                  onClick={onCancel}
                  className="button-rounded-lg no-border">
                  {t('cancel')}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    handleSubmit()
                  }}
                  disabled={_.isEmpty(values)}
                  className="button-rounded-lg no-border">
                  {t('add content')}
                </Button>
              </div>
            </React.Fragment>
          )
        }}
      </Formik>
    </div>
  )
}

export const useCodiMentions = ({
                                  open = false,
  onSubmit = NullAsDefault,
  onCancel = NullAsDefault,
  ...props
}) => {
  const modal = useMemo(
    () => (
      <Modal
        width={600}
        footer={null}
        open={open}
        onCancel={onCancel}
        maskClosable={false}
        destroyOnHidden={true}
        className="NoFooter custom-modal"
        {...props}>
        <ModalContent
          onCancel={onCancel}
          onSubmit={(values, callback = NullAsDefault) => {
            onSubmit(values)
            callback()
            onCancel()
          }}
        />
      </Modal>
    ),
    [open]
  )
  return {
    modal,
  }
}
